<script setup>
const props = defineProps({
  thumbnail: {
    type: Object,
    default: null,
  },
  title: {
    type: String,
    required: true,
  },
  copy: {
    type: String,
    default: '',
  },
  expandButtonGtmClass: {
    type: String,
    default: null,
  },
})

const { t } = useI18n()
const headerHeight = ref(0)
const headerRef = ref(null)
const bodyRef = ref(null)

const isExpanded = ref(false)
const buttonLabel = computed(() =>
  isExpanded.value === true
    ? t('cardDetails.collapse')
    : t('cardDetails.expand')
)

// Handlers
function handleClick() {
  isExpanded.value = !isExpanded.value
  bodyRef.value.scrollTo(0, 0)
}

function handleResize() {
  headerHeight.value = headerRef.value.getBoundingClientRect().height
}

// Lifecycle
onMounted(() => {
  handleResize()
  window.addEventListener('resize', handleResize)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize)
})
</script>

<template>
  <article
    class="card-details"
    :class="{ 'card-details--expanded': isExpanded }"
    :style="{
      '--y': headerHeight,
    }"
  >
    <header ref="headerRef" class="card-details__header">
      <img v-if="thumbnail" :src="thumbnail.url" :alt="thumbnail.title" />
    </header>

    <div class="card-details__content">
      <div ref="bodyRef" class="card-details__body">
        <div class="card-details__title">
          <div class="card-details__pulse-container">
            <PulseBeat class="card-details__pulse" />
          </div>
          <p>{{ title }}</p>
        </div>
        <p class="card-details__copy">{{ copy }}</p>
      </div>

      <div class="card-details__footer">
        <button
          class="card-details__expand"
          :class="expandButtonGtmClass"
          @click="handleClick"
        >
          <Icon name="plus" class="card-details__expand-icon" />
          <p class="text__eyebrow-medium">{{ buttonLabel }}</p>
        </button>
      </div>
    </div>
  </article>
</template>

<style lang="scss">
@use 'sass:math';

// Vendors
@import 'styles/vendors/sass-mq/mq';

// Utils
@import 'styles/utils/variables';
@import 'styles/utils/functions';

.card-details {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 358px;
  position: relative;
  overflow: hidden;

  @include mq($from: 'l') using($from) {
    height: 466px;
  }

  &__header {
    aspect-ratio: 318 / 190;
    background: black;
    width: 100%;
    flex-shrink: 0;

    @include mq($from: 'l') using($from) {
      aspect-ratio: 314 / 210;
    }
  }

  &__header img {
    object-fit: cover;
    display: block;
    width: 100%;
    height: 100%;
  }

  &__content {
    aspect-ratio: 318 / 168;
    padding: 0 30px;
    display: flex;
    flex-direction: column;
    flex-grow: 1;

    @include mq($from: 'l') using($from) {
      aspect-ratio: 314 / 256;
      padding: 0 30px;

      .carousel & {
        aspect-ratio: 314 / 188;
      }
    }
  }

  &__body {
    background-color: #1c1229;
    padding: 24px 24px 72px;
    width: 100%;
    height: 100%;
    position: absolute;
    left: 0;
    overflow: hidden;
    transition: transform 0.3s;

    @include mq($from: 'l') using($from) {
      padding: 30px;
    }
  }

  &__pulse-container {
    width: 0px;
    height: 25px;
    z-index: 0;
  }

  &__title {
    display: flex;
    color: $brand-white;
    position: relative;
    z-index: 1;

    font-weight: 400;
    font-size: 17px;
    line-height: 24px;
    letter-spacing: -0.01em;

    @include mq($from: 'l') using($from) {
      font-size: 16px;
      line-height: 25px;
    }
  }

  &__copy {
    margin-top: 8px;
    opacity: 0;
    transition: opacity 0.3s;
  }

  &__footer {
    background: linear-gradient(to top, #1c1229 75%, rgba(#1c1229, 0) 100%);
    padding: 30px 0 32px;
    width: calc(100% - gs(1, 0, 'xxxs') * 2);
    position: absolute;
    bottom: 0;
    z-index: 1;

    @include mq($from: 'l') using($from) {
      width: calc(100% - gs(1, 0, $from) * 2);
    }
  }

  &__footer button {
    display: flex;
    align-items: center;
  }

  &__footer svg {
    color: #c80afb;
    width: 14px;
    height: 14px;
    transition: transform 0.3s;
  }

  &__footer p {
    color: $brand-white;
    margin-left: 15px;
  }

  &__expand {
    &-icon {
      transition: transform 0.25s;
    }
    &:hover {
      .card-details__expand-icon {
        transform: rotate(-45deg);
      }
    }
  }

  &--expanded {
    .card-details__body {
      transform: translateY(calc((var(--y) * -1px) - 1px));
      overflow-y: auto;
    }

    .card-details__copy {
      opacity: 1;
    }

    .card-details__expand-icon {
      transform: rotate(45deg);
    }
  }
}
</style>
